import React, { ReactElement, useEffect, useState } from 'react';

import { useIdleTimer } from 'react-idle-timer';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  useDisclosure,
  ButtonGroup,
} from '@chakra-ui/react';

const fiftyFiveMins = Number(55 * 60 * 1000);
const throttle = 500;
const twoMins = Number(2 * 60 * 1000);
const oneMinuteinSeconds = 60;

export interface IdleTimerProps {
  promptBeforeIdle?: number;
  timeout?: number;
  onLogout: () => void;
  children: ReactElement;
}

function IdleTimer(props: IdleTimerProps): JSX.Element {
  const { onLogout, children, timeout: timeoutProp, promptBeforeIdle: promptBeforeIdleProp } = props;
  const [remaining, setRemaining] = useState<string>('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const timeout = timeoutProp ?? fiftyFiveMins;
  const promptBeforeIdle = promptBeforeIdleProp ?? twoMins;

  useEffect(() => {
    const interval = setInterval(() => {
      const remainingSeconds = Math.ceil(getRemainingTime() / 1000);
      if (remainingSeconds > oneMinuteinSeconds) {
        setRemaining('2 minutes');
      } else if (remainingSeconds < oneMinuteinSeconds && remainingSeconds >= 10) {
        setRemaining('1 minute');
      } else {
        setRemaining('a few seconds');
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });

  const onIdle = async () => {
    onClose();
    // Tell all tabs to log the user out.
    // Passing true as a second parameter will
    // also emit the event in this tab.
    message({ action: 'LOGOUT_USER' }, true);
  };

  const onMessage = async (data: { action: string }) => {
    switch (data.action) {
      case 'LOGOUT_USER':
        await onLogout();
        break;
      default: // no op
    }
  };

  const onActive = () => {
    onClose();
  };

  const onPrompt = () => {
    onOpen();
  };

  const { getRemainingTime, activate, message } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    onMessage,
    timeout,
    promptBeforeIdle,
    throttle,
    crossTab: true,
    name: 'CAS-Shield',
    syncTimers: 200,
    leaderElection: true,
  });

  const handleStillHere = () => {
    activate();
  };

  return (
    <React.Fragment>
      {children}

      <Modal isOpen={isOpen} onClose={handleStillHere}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>You will be logged out soon</ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text mb={0}>
              To protect sensitive information, you will be logged out in <b role="timer">{remaining}</b>.
            </Text>
          </ModalBody>

          <ModalFooter>
            <ButtonGroup spacing={3} size={'lg'}>
              <Button variant={'secondary'} onClick={onLogout}>
                Log out
              </Button>

              <Button variant={'solid'} onClick={handleStillHere}>
                Stay logged in
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </React.Fragment>
  );
}

export { IdleTimer };
